import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { MixIcon, PauseIcon, PlayIcon, StopIcon, TrashIcon } from '@radix-ui/react-icons';
import { MicIcon } from 'lucide-react';
import { type FC, useEffect, useRef, useState } from 'react';

import { FieldConfirmSheet } from '../../../../../components/FieldConfirmSheet';
import { useFieldLayout } from '../../../../../hooks/useMedia';
import { formatDurationFromSeconds } from '../../../../../tools/formatTimeUtils';
import { useAudioRecorder } from './useAudioRecorder';

/** One audio note per note — the field app only renders the first one. */
const SINGLE_AUDIO_HINT = 'Remove the current audio note to record another';

interface MicPermissionDialogProps {
  message: string | null;
  onClose: () => void;
}

/** The microphone permission-denied explainer shared by every recorder surface. */
export const MicPermissionDialog: FC<MicPermissionDialogProps> = ({ message, onClose }) => {
  const isFieldLayout = useFieldLayout();
  // Phones and tablets: a bottom sheet with the same two ways out.
  if (isFieldLayout) {
    return (
      <FieldConfirmSheet
        open={!!message}
        onOpenChange={(open) => !open && onClose()}
        title="Microphone access required"
        description={message}
        confirmLabel="Try again"
        cancelLabel="Close"
        onConfirm={onClose}
      />
    );
  }
  return (
    <AlertDialog open={!!message} onOpenChange={(open: boolean) => !open && onClose()}>
      <AlertDialogContent className="bg-card">
        <AlertDialogHeader>
          <AlertDialogTitle>Microphone access required</AlertDialogTitle>
          <AlertDialogDescription className="text-left">{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Close</AlertDialogCancel>
          <AlertDialogAction onClick={onClose}>Try again</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

interface AudioRecorderProps {
  isRecording: boolean;
  setIsRecording: (isRecording: boolean) => void;
  audioBlob: Blob | null;
  setAudioBlob: (blob: Blob | null) => void;
  /** Matches Photo/Video capture tiles in narrow grids. */
  triggerVariant?: 'default' | 'tile';
  /** Elapsed seconds at the moment recording stopped — the reliable duration source. */
  onRecordingComplete?: (seconds: number) => void;
  disabled?: boolean;
}

/**
 * Capture control only: start recording, stop recording, or sit disabled while a recording
 * already exists. The finished recording is rendered separately by `AudioNoteRow`, which
 * consumers place below the capture grid.
 */
export const AudioRecorder = ({
  isRecording,
  setIsRecording,
  audioBlob,
  setAudioBlob,
  triggerVariant = 'default',
  onRecordingComplete,
  disabled = false,
}: AudioRecorderProps) => {
  const isTile = triggerVariant === 'tile';

  const recorder = useAudioRecorder({
    onComplete: (blob, seconds) => {
      setAudioBlob(blob);
      onRecordingComplete?.(seconds);
    },
  });

  // The parent owns the isRecording flag its other controls key off; mirror the hook into it.
  useEffect(() => {
    setIsRecording(recorder.isRecording);
  }, [recorder.isRecording, setIsRecording]);

  const tileClassName = 'flex h-[5.5rem] w-full flex-col items-center justify-center gap-2 py-4';

  const control = recorder.isRecording ? (
    <div
      className={
        isTile
          ? `border-input rounded-md border ${tileClassName}`
          : // Same 36px box as the idle button, so the cell never changes height
            'border-input flex h-9 w-full items-center justify-between gap-2 rounded-md border px-2'
      }
    >
      <div className="flex items-center gap-2">
        <span className="relative flex items-center">
          <span className="bg-destructive h-3 w-3 animate-pulse rounded-full" />
          <span className="bg-destructive/30 absolute -inset-1 animate-ping rounded-full" />
        </span>
        <span
          className={`font-semibold tabular-nums text-gray-900 ${isTile ? 'text-base' : 'text-sm'}`}
        >
          {formatDurationFromSeconds(recorder.elapsedSeconds)}
        </span>
      </div>
      <Button
        type="button"
        variant="destructive"
        size={isTile ? 'sm' : 'xs'}
        onClick={recorder.stop}
        className="flex items-center gap-2"
      >
        <StopIcon />
        Stop
      </Button>
    </div>
  ) : (
    <Button
      type="button"
      variant="outline"
      onClick={() => void recorder.start()}
      disabled={disabled || recorder.isStarting || !!audioBlob}
      className={isTile ? tileClassName : 'flex w-full items-center justify-center gap-2 py-2'}
    >
      {isTile ? <MicIcon className="h-6 w-6" /> : <MixIcon />}
      {recorder.isStarting ? 'Starting...' : isTile ? 'Audio' : 'Record Audio'}
    </Button>
  );

  return (
    <>
      {audioBlob && !recorder.isRecording ? (
        <TooltipProvider>
          <Tooltip>
            {/* Disabled buttons swallow pointer events, so the wrapper carries the tooltip */}
            <TooltipTrigger asChild>
              <span className="block w-full">{control}</span>
            </TooltipTrigger>
            <TooltipContent>{SINGLE_AUDIO_HINT}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        control
      )}

      <MicPermissionDialog
        message={recorder.permissionError}
        onClose={recorder.dismissPermissionError}
      />
    </>
  );
};

interface AudioNoteRowProps {
  audioBlob: Blob;
  onClear: () => void;
  /** Seconds from the recorder's own counter; MediaRecorder blobs report Infinity in Chrome. */
  durationSeconds?: number;
  readOnly?: boolean;
  /** Blocks playback too, not just removal — e.g. while the note is saving. */
  disabled?: boolean;
}

/** The finished recording: play, duration, remove. Sits below the capture tiles. */
export const AudioNoteRow: FC<AudioNoteRowProps> = ({
  audioBlob,
  onClear,
  durationSeconds,
  readOnly = false,
  disabled = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [measuredSeconds, setMeasuredSeconds] = useState<number | null>(null);
  /** Position while playing (or paused mid-way); null when idle at the start. */
  const [playbackSeconds, setPlaybackSeconds] = useState<number | null>(null);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(audioBlob);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audioBlob]);

  // Only needed when the recorder did not hand us its elapsed count
  useEffect(() => {
    if (durationSeconds !== undefined) return;
    let cancelled = false;

    const measure = async () => {
      try {
        const context = new AudioContext();
        const decoded = await context.decodeAudioData(await audioBlob.arrayBuffer());
        if (!cancelled) setMeasuredSeconds(decoded.duration);
        void context.close();
      } catch (error) {
        console.error('Could not measure audio duration:', error);
      }
    };

    void measure();
    return () => {
      cancelled = true;
    };
  }, [audioBlob, durationSeconds]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    audio.src = audioUrl;
    const onEnded = () => {
      setIsPlaying(false);
      audio.currentTime = 0;
      setPlaybackSeconds(null);
    };
    const onTimeUpdate = () => setPlaybackSeconds(audio.currentTime);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [audioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch((error) => {
        console.error('Playback error:', error);
        setIsPlaying(false);
      });
  };

  const seconds = durationSeconds ?? measuredSeconds;

  return (
    <div className="border-input bg-card flex w-full min-w-0 items-center gap-3 rounded-md border p-3">
      <audio ref={audioRef} className="hidden" />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={togglePlay}
        disabled={disabled}
        title={isPlaying ? 'Pause' : 'Play'}
        aria-label={isPlaying ? 'Pause audio note' : 'Play audio note'}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </Button>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">Audio note</p>
        <p className="text-xs tabular-nums text-gray-600">
          {seconds === null
            ? '—'
            : playbackSeconds === null
              ? formatDurationFromSeconds(Math.round(seconds))
              : `${formatDurationFromSeconds(Math.round(playbackSeconds))} / ${formatDurationFromSeconds(Math.round(seconds))}`}
        </p>
      </div>

      {!readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-gray-500 hover:text-red-600"
          onClick={() => setIsClearConfirmOpen(true)}
          disabled={disabled}
          title="Remove audio note"
          aria-label="Remove audio note"
        >
          <TrashIcon />
        </Button>
      )}

      <AlertDialog open={isClearConfirmOpen} onOpenChange={setIsClearConfirmOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this recording?</AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              The audio you recorded will be removed and cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsPlaying(false);
                onClear();
                setIsClearConfirmOpen(false);
              }}
            >
              Clear audio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
